import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from './Header';
import Footer from './Footer';
import ProfileNav from './ProfileNav';
import { getApiHost, apiFetch } from '../utils/api';
import { hasDeepDiverAccess, DNET_DEX_URL, TIER_THRESHOLDS, SUBSCRIPTION_PRICES, formatDnetAmount } from '../utils/tiers';
import { trackEvent } from '../utils/analytics';

const API_BASE_URL = getApiHost();
const MAX_KEYS = 2;

interface ApiKey {
  _id: string;
  key: string;
  name: string;
  createdAt: string;
  lastUsedAt: string | null;
  walletAddress: string;
}

const ApiKeysPage: React.FC = () => {
  const { user, isAuthenticated, refreshUser, openLogin } = useAuth();

  // Refresh user data on mount for latest DNET balance and tier
  useEffect(() => {
    if (isAuthenticated) refreshUser();
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const getAuthHeaders = (): HeadersInit => {
    const token = localStorage.getItem('deepnets_token');
    return token
      ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      : { 'Content-Type': 'application/json' };
  };

  const fetchApiKeys = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/api-keys`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setApiKeys(data);
      }
    } catch (error) {
      console.error('Error fetching API keys:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) fetchApiKeys();
  }, [isAuthenticated, fetchApiKeys]);

  const createApiKey = async () => {
    if (!isAuthenticated || !newKeyName.trim()) return;
    setCreating(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/api-keys`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ name: newKeyName.trim() }),
      });

      if (response.ok) {
        const data = await response.json();
        setNewlyCreatedKey(data.key);
        setNewKeyName('');
        setShowCreateForm(false);
        // Refresh the list (will show masked version)
        fetchApiKeys();
      } else {
        const err = await response.json();
        alert(err.error || 'Failed to create API key');
      }
    } catch (error) {
      console.error('Error creating API key:', error);
      alert('Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/api-keys/${deleteTarget.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        setApiKeys(prev => prev.filter(k => k._id !== deleteTarget.id));
      } else {
        alert('Failed to delete API key');
      }
    } catch (error) {
      console.error('Error deleting API key:', error);
      alert('Failed to delete API key');
    } finally {
      setDeleteTarget(null);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  const formatTimeAgo = (dateStr: string) => {
    const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  // Unauthenticated state
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-crypto-dark text-white flex flex-col">
        <Header />
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
          <div className="text-center">
            <svg className="w-16 h-16 text-gray-600 mx-auto mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            <h1 className="text-3xl font-bold mb-3">API Keys</h1>
            <p className="text-gray-400 mb-8 max-w-md mx-auto">
              Connect your wallet to create API keys for programmatic access to deepnets.ai research.
            </p>
            <button
              onClick={() => openLogin('api_keys_gate')}
              className="bg-crypto-accent hover:bg-crypto-accent/80 text-crypto-dark px-6 py-3 rounded-lg font-medium transition-all"
            >
              Connect Wallet
            </button>
            <Link to="/" className="block mt-4 text-gray-500 hover:text-white transition-colors text-sm">
              ← Back to home
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const canCreateMore = apiKeys.length < MAX_KEYS;
  const isDeepDiver = hasDeepDiverAccess(user?.tier);

  const handleSubscribe = async () => {
    try {
      const token = localStorage.getItem('deepnets_token');
      const response = await apiFetch(`${API_BASE_URL}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ tier: 'deep_diver' }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to create checkout session');
      }
      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      console.error('Subscription error:', error);
      alert(error instanceof Error ? error.message : 'Failed to start subscription');
    }
  };

  // Gate: Deep Diver required for API key management
  if (!isDeepDiver) {
    const dnetBalance = user?.dnetBalance || 0;
    const needed = Math.max(0, TIER_THRESHOLDS.deep_diver - dnetBalance);
    return (
      <div className="min-h-screen bg-crypto-dark text-white flex flex-col">
        <Header />
        <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-8 w-full">
          <h1 className="text-2xl font-bold text-white mb-6">Account</h1>
          <ProfileNav />
          <div className="text-center max-w-2xl mx-auto py-8">
            <div className="text-5xl mb-4">🔱</div>
            <h2 className="text-2xl font-bold mb-3">Deep Diver Required</h2>
            <p className="text-gray-400 mb-6">
              API key access requires the Deep Diver tier. Hold {formatDnetAmount(TIER_THRESHOLDS.deep_diver)} $DNET or subscribe (${SUBSCRIPTION_PRICES.deep_diver}/mo) to unlock full programmatic access to deepnets.ai research.
            </p>
            {dnetBalance > 0 && (
              <p className="text-gray-500 text-sm mb-4">
                Your balance: {formatDnetAmount(dnetBalance)} $DNET — need {formatDnetAmount(needed)} more
              </p>
            )}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href={DNET_DEX_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('buy_dnet_clicked', { source: 'api_keys_gate' })}
                className="bg-purple-600 hover:bg-purple-500 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Buy $DNET →
              </a>
              <button
                onClick={handleSubscribe}
                className="bg-crypto-accent hover:bg-crypto-accent/80 text-crypto-dark px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Subscribe ${SUBSCRIPTION_PRICES.deep_diver}/mo
              </button>
            </div>
            <Link to="/tiers" className="block mt-4 text-gray-500 hover:text-white transition-colors text-sm">
              View all tiers →
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-crypto-dark text-white flex flex-col">
      <Header />
      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-8 w-full">
        <h1 className="text-2xl font-bold text-white mb-6">Account</h1>
        <ProfileNav />

        {/* Security notice — shown until the user re-issues a key. All keys were
            revoked on 2026-07-07 after a resolved infrastructure exposure. */}
        {apiKeys.length === 0 && (
          <div className="mb-6 rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-4">
            <div className="flex items-start gap-3">
              <span className="text-yellow-300 mt-0.5">🛡️</span>
              <div className="text-sm">
                <div className="font-semibold text-yellow-200 mb-1">API keys were reset for security</div>
                <p className="text-gray-300">
                  On July 7, 2026 we resolved an infrastructure issue that could have exposed API keys and,
                  as a precaution, <strong className="text-white">revoked all existing keys</strong>. If you had a
                  key it has stopped working — please generate a new one below and update your integrations.
                  Sorry for the disruption.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Section header */}
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <h2 className="text-xl font-semibold text-white">API Keys</h2>
            <p className="text-gray-400 text-sm mt-1">
              Access deepnets.ai programmatically at <span className="font-mono text-gray-300">api.deepnets.ai</span>. Fresh token research costs 1 credit.
              {' '}<Link to="/api-docs" className="text-crypto-accent hover:text-white transition-colors">View API docs →</Link>
            </p>
            <p className="text-gray-500 text-xs mt-1">
              {apiKeys.length} / {MAX_KEYS} keys used
              {user?.credits !== undefined && (
                <span className="ml-3">·  {user.credits} credits remaining</span>
              )}
            </p>
          </div>
          {canCreateMore && !showCreateForm && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="bg-crypto-accent hover:bg-crypto-accent/80 text-crypto-dark px-5 py-2.5 rounded-lg font-medium transition-all whitespace-nowrap"
            >
              + New Key
            </button>
          )}
        </div>

        {/* Newly created key banner */}
        {newlyCreatedKey && (
          <div className="mb-6 bg-green-500/10 border border-green-500/30 rounded-lg p-5">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="font-semibold text-green-400">API Key Created</h3>
                </div>
                <p className="text-gray-300 text-sm mb-3">
                  Copy your key now — you won't be able to see it again.
                </p>
                <div className="flex items-center gap-2">
                  {/* data-mask: keep the raw key out of PostHog session
                      recordings (rrweb masks [data-mask] text). Without this the
                      full dn_ key was captured into replays on every creation. */}
                  <code data-mask className="bg-crypto-dark px-4 py-2 rounded-lg text-sm font-mono text-white break-all flex-1 select-all">
                    {newlyCreatedKey}
                  </code>
                  <button
                    onClick={() => copyToClipboard(newlyCreatedKey)}
                    className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors whitespace-nowrap"
                  >
                    {copied ? '✓ Copied' : 'Copy'}
                  </button>
                </div>
              </div>
              <button
                onClick={() => setNewlyCreatedKey(null)}
                className="text-gray-500 hover:text-white ml-4"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Create form */}
        {showCreateForm && (
          <div className="mb-6 bg-crypto-gray/50 border border-gray-700 rounded-lg p-5">
            <h2 className="text-lg font-semibold mb-3">Create New API Key</h2>
            <div className="flex gap-3">
              <input
                type="text"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="Key name (e.g. 'My Trading Bot')"
                className="flex-1 bg-crypto-dark border border-gray-600 rounded-lg px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-crypto-accent transition-colors"
                onKeyDown={(e) => e.key === 'Enter' && createApiKey()}
                autoFocus
                maxLength={50}
              />
              <button
                onClick={createApiKey}
                disabled={creating || !newKeyName.trim()}
                className="bg-crypto-accent hover:bg-crypto-accent/80 disabled:opacity-50 disabled:cursor-not-allowed text-crypto-dark px-5 py-2.5 rounded-lg font-medium transition-all"
              >
                {creating ? 'Creating...' : 'Create'}
              </button>
              <button
                onClick={() => { setShowCreateForm(false); setNewKeyName(''); }}
                className="text-gray-400 hover:text-white px-3 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Loading state */}
        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-crypto-accent"></div>
            <p className="mt-4 text-gray-400">Loading API keys...</p>
          </div>
        ) : apiKeys.length === 0 ? (
          /* Empty state */
          <div className="text-center py-16">
            <svg className="w-16 h-16 text-gray-700 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            <h2 className="text-xl font-semibold mb-2">No API Keys</h2>
            <p className="text-gray-400 mb-6 max-w-sm mx-auto">
              Create an API key to start querying token safety data programmatically.
            </p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="bg-crypto-accent hover:bg-crypto-accent/80 text-crypto-dark px-6 py-3 rounded-lg font-medium transition-all"
            >
              Create Your First Key
            </button>
          </div>
        ) : (
          /* Key list */
          <div className="space-y-3">
            {apiKeys.map((apiKey) => (
              <div
                key={apiKey._id}
                className="bg-crypto-gray/50 border border-gray-700 rounded-lg p-5 hover:border-gray-600 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-lg truncate">{apiKey.name}</h3>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-500/10 text-green-400 border border-green-500/20">
                        Active
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-400">
                      <code className="bg-crypto-dark px-2 py-0.5 rounded font-mono text-gray-300">
                        {apiKey.key}
                      </code>
                      <span>Created {formatDate(apiKey.createdAt)}</span>
                      {apiKey.lastUsedAt && (
                        <span>Last used {formatTimeAgo(apiKey.lastUsedAt)}</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setDeleteTarget({ id: apiKey._id, name: apiKey.name })}
                    className="ml-4 text-gray-500 hover:text-red-400 transition-colors p-2"
                    title="Delete key"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Usage guide */}
        <div className="mt-12 bg-crypto-gray/30 border border-gray-700/50 rounded-lg p-6">
          <h3 className="font-semibold text-lg mb-3">Quick Start</h3>
          <p className="text-gray-400 text-sm mb-4">
            Pass your API key in the <code className="text-gray-300 bg-crypto-dark px-1.5 py-0.5 rounded">x-api-key</code> header.
            Existing token data is free. New research costs 1 credit.
          </p>
          <div className="bg-crypto-dark rounded-lg p-4 overflow-x-auto">
            <pre className="text-sm font-mono text-gray-300 whitespace-pre">{`curl https://api.deepnets.ai/api/token-safety/YOUR_MINT_ADDRESS \\
  -H "x-api-key: YOUR_API_KEY"`}</pre>
          </div>
        </div>

        {/* Delete confirmation modal */}
        {deleteTarget && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setDeleteTarget(null)} />
            <div className="relative bg-crypto-gray border border-gray-700 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
              <div className="text-center">
                <div className="w-12 h-12 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Delete API Key</h3>
                <p className="text-gray-400 mb-1">
                  Are you sure you want to delete
                </p>
                <p className="text-white font-medium mb-6">"{deleteTarget.name}"?</p>
                <p className="text-gray-500 text-sm mb-6">
                  Any applications using this key will lose access immediately.
                </p>
                <div className="flex justify-center gap-3">
                  <button
                    onClick={() => setDeleteTarget(null)}
                    className="bg-gray-700 hover:bg-gray-600 text-white px-5 py-2.5 rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-lg font-medium transition-colors"
                  >
                    Delete Key
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default ApiKeysPage;
